import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

const CATEGORY_LABELS = {
  moderation: "Moderación",
  novedades: "Novedades",
  nuevos_lugares: "Lugares nuevos",
  general: "General",
};

const relativeTime = value => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return "Ahora";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Hace ${days} d`;
  return date.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

export default function NotificationInbox({ userId, onNavigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const notificationsTable = useCallback(() => {
    try { return typeof supabase?.from === "function" ? supabase.from("notifications") : null; }
    catch { return null; }
  }, []);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true); setError("");
    try {
      const table = notificationsTable();
      if (!table || typeof table.select !== "function") { setLoading(false); return; }
      const { data, error: queryError } = await table
        .select("id,category,title,body,deep_link,data,created_at,read_at,push_dispatched_at,push_error")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (queryError) setError(queryError.message);
      else setItems(data || []);
    } catch (loadError) { setError(loadError?.message || "No se pudo cargar el historial."); }
    finally { setLoading(false); }
  }, [notificationsTable, userId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!userId || typeof supabase?.channel !== "function") return undefined;
    let channel;
    try {
      channel = supabase.channel(`notifications:${userId}`)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, payload => {
          setItems(current => [payload.new, ...current.filter(item => item.id !== payload.new.id)].slice(0, 50));
        })
        .subscribe();
    } catch { return undefined; }
    return () => { if (channel && typeof supabase?.removeChannel === "function") supabase.removeChannel(channel); };
  }, [userId]);

  const unread = useMemo(() => items.filter(item => !item.read_at).length, [items]);

  const markRead = async item => {
    if (!item.read_at) {
      const now = new Date().toISOString();
      setItems(current => current.map(row => row.id === item.id ? { ...row, read_at: now } : row));
      try {
        const table = notificationsTable();
        if (table && typeof table.update === "function") await table.update({ read_at: now }).eq("id", item.id).eq("user_id", userId);
      } catch { /* local read state remains responsive */ }
    }
    if (item.deep_link && onNavigate) onNavigate(item.deep_link, item.id);
  };

  const markAllRead = async () => {
    const now = new Date().toISOString();
    setItems(current => current.map(item => item.read_at ? item : { ...item, read_at: now }));
    try {
      const table = notificationsTable();
      if (table && typeof table.update === "function") await table.update({ read_at: now }).eq("user_id", userId).is("read_at", null);
    } catch { /* local read state remains responsive */ }
  };

  return (
    <section aria-label="Historial de notificaciones" style={{ background: "#161616", borderRadius: 12, padding: 14, marginBottom: 14, border: "1px solid #222" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div>
          <p style={{ margin: 0, fontSize: 11, color: "#e53935", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em" }}>Avisos de Venaqqi</p>
          <p style={{ margin: "4px 0 0", fontSize: 10, color: "#666" }}>{unread ? `${unread} sin leer · ` : ""}Aquí permanecen aunque Push esté apagado.</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={load} disabled={loading} style={{ border: "1px solid #333", background: "#111", color: "#aaa", borderRadius: 8, padding: "6px 8px", fontSize: 9, cursor: "pointer" }}>↻</button>
          {unread > 0 && <button type="button" onClick={markAllRead} style={{ border: "1px solid #333", background: "#111", color: "#aaa", borderRadius: 8, padding: "6px 8px", fontSize: 9, cursor: "pointer" }}>Leer todo</button>}
        </div>
      </div>

      {loading && <p style={{ color: "#666", fontSize: 10, margin: 0 }}>Cargando avisos…</p>}
      {error && <p style={{ color: "#e57373", fontSize: 10, margin: 0 }}>No se pudo cargar el historial.</p>}
      {!loading && !error && items.length === 0 && <p style={{ color: "#555", fontSize: 10, margin: 0 }}>Todavía no tienes avisos.</p>}

      {!loading && items.length > 0 && <div style={{ display: "grid", gap: 7, maxHeight: 300, overflowY: "auto" }}>
        {items.map(item => {
          const unreadItem = !item.read_at;
          return <button key={item.id} type="button" onClick={() => markRead(item)} style={{ textAlign: "left", background: unreadItem ? "#1d1717" : "#111", border: `1px solid ${unreadItem ? "#5a2b2b" : "#242424"}`, borderRadius: 10, padding: "10px 11px", cursor: "pointer", color: "inherit" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
              <span style={{ color: unreadItem ? "#ff8a80" : "#777", fontSize: 9, fontWeight: 800 }}>{CATEGORY_LABELS[item.category] || "Aviso"}{unreadItem ? " · NUEVO" : ""}</span>
              <span style={{ color: "#555", fontSize: 9 }}>{relativeTime(item.created_at)}</span>
            </div>
            <p style={{ margin: "5px 0 2px", color: "#ddd", fontSize: 11, fontWeight: 800 }}>{item.title}</p>
            <p style={{ margin: 0, color: "#888", fontSize: 10, lineHeight: 1.45 }}>{item.body}</p>
          </button>;
        })}
      </div>}
    </section>
  );
}
